import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useAttributeCatalog } from '../../../../components/AttributeOptions/useAttributeCatalog';
import { AttributeValueOption } from './attribute.types';
import { FocusOption } from '../AffinityNetwork/CategoryPicker';

interface Props {
  value: FocusOption | null;
  onChange: (value: FocusOption | null) => void;
}

// Flattens the attribute catalog into a single searchable value list grouped by
// attribute group. Used to focus the read-only attribute-option hub view.
export function useAttributeValueOptions(): AttributeValueOption[] {
  const { attributes } = useAttributeCatalog();
  return useMemo(() => {
    const out: AttributeValueOption[] = [];
    attributes.forEach((attr) => {
      (attr.values || []).forEach((v) => {
        out.push({ id: v.id, name: v.name, attributeId: attr.id, attributeName: attr.name });
      });
    });
    return out;
  }, [attributes]);
}

export const AttributeValuePicker: React.FC<Props> = ({ value, onChange }) => {
  const options = useAttributeValueOptions();

  return (
    <Autocomplete
      value={value ? (options.find((o) => o.id === value.id) ?? null) : null}
      onChange={(_, v) => onChange(v ? { id: v.id, name: v.name } : null)}
      options={options}
      groupBy={(o) => o.attributeName}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField {...params} label="Attribute option" placeholder="Search…" size="small" />
      )}
      sx={{ width: '100%' }}
    />
  );
};
